'use client';
// Demandes de rendez-vous — les demandes déposées sur le site (formulaire et
// fenêtre « Prendre rendez-vous »), lues aussi dans Qualité (Ines, 14/09/2026).
//
// ── UN SEUL ÉCRAN DEPUIS LE 05/10/2026 ──────────────────────────────────────
// Le site DÉPOSE les demandes et prévient par courriel ; il ne les traite plus.
// « Demandes reçues » (`site/src/app/demandes/`) a été SUPPRIMÉ — décision
// d'Inès, même arbitrage que les demandes de compte le 13/09 : deux listes et
// deux boutons « Confirmer » sur le même enregistrement laissaient deux
// personnes trancher la même demande, la seconde trouvant un état déjà changé
// sans savoir par qui.
//
// ⚠️ NE PAS ROUVRIR UNE SECONDE PORTE. Tout ce que le site savait faire et qui
// avait un usage a été porté ici AVANT le retrait : le journal par demande
// (`JournalDemande`). Ce qui ne l'a pas été est dit à cet endroit, nommément.
//
// ── Ce qui n'est PAS chargé avec la liste ───────────────────────────────────
// L'ordonnance est stockée en base64 (jusqu'à 6 Mo) : la liste demande ses
// champs explicitement et ne la remonte jamais. Elle n'est lue qu'au clic
// « Voir l'ordonnance », pour CETTE demande — même règle que le site.
//
// ── CONFIRMER N'EST PLUS UN CLIC (Inès, 05/10/2026) ─────────────────────────
// « Quand on confirme un rendez-vous, il faut mentionner les infos qu'on a
// récoltées du patient au moment du coup de fil. » Le bouton ouvre donc un
// pop-up, et le statut ne passe à `confirmed` QU'AVEC ces faits. Les règles de
// saisie vivent dans `@/lib/rdv-confirmation` — module pur, éprouvé par
// `backend/tests/front-rdv-confirmation.test.js` — et non ici : le JSX ne
// garde que le rendu.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchEcriture } from '@/lib/ecrire';
import {
  card, EmptyState, FieldError, FilterBar, Modale, RequiredHint, Skeleton, inputStyle, useBusy,
} from '@/components/ui';
import { useFormulaire } from '@/components/Formulaire';
import {
  LIBELLES_LIEU, champsConfirmation, depart, estConfirme,
  identiteConfirmee, normaliser, resumeConfirmation,
  type ConfirmationRdv, type LieuRdv, type SaisieConfirmation,
} from '@/lib/rdv-confirmation';
import { journalDe, type LigneJournal } from '@/lib/suivi-demande';
import { useGed } from '@/lib/store';
import { H1, lbl } from './ressources/commun';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { jourIso, aujourdhuiIso, fmtDateHeure } from '@/lib/meta';
type Statut = 'new' | 'confirmed' | 'done' | 'cancelled' | 'archived';
type Demande = {
  documentId: string; ref: string; rdvStatus: Statut;
  patientName: string; patientPhone: string; patientEmail: string;
  preferredDate: string; preferredSlot: string; requestedTests: string; notes: string;
  prescriptionFileName: string; createdAt: string;
  /** Ce que le coup de fil a établi. Absent tant que la demande n'a pas été
   *  confirmée — et les demandes confirmées AVANT le 05/10/2026 n'en portent
   *  pas : l'écran doit savoir se taire, pas afficher « le — à — ». */
  confirmation?: ConfirmationRdv | null;
};

const STATUTS: Record<Statut, { label: string; bg: string; fg: string }> = {
  new: { label: 'Nouvelle', bg: 'var(--cyan-50)', fg: 'var(--cyan-700)' },
  confirmed: { label: 'Confirmée', bg: 'var(--blue-50)', fg: 'var(--blue-700)' },
  done: { label: 'Traitée', bg: 'var(--green-50)', fg: 'var(--green-600)' },
  cancelled: { label: 'Annulée', bg: 'var(--gray-50)', fg: 'var(--text-muted)' },
  archived: { label: 'Archivée', bg: 'var(--gray-100)', fg: 'var(--text-muted)' },
};
const EN_COURS: Statut[] = ['new', 'confirmed'];
const CRENEAUX: Record<string, string> = { morning: 'Matin', afternoon: 'Après-midi', evening: 'Soirée' };
const CHAMPS = ['ref', 'rdvStatus', 'patientName', 'patientPhone', 'patientEmail', 'preferredDate', 'preferredSlot', 'requestedTests', 'notes', 'prescriptionFileName', 'createdAt', 'confirmation'];

const dateFr = (iso: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso || '—';
};
const depuis = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : fmtDateHeure(d);
};

async function lireToutes(): Promise<Demande[]> {
  const q = CHAMPS.map((c, i) => `fields[${i}]=${c}`).join('&');
  const tout: Demande[] = [];
  for (let page = 1; page <= 20; page += 1) {
    const r = await fetch(`/api/ged/appointments?${q}&sort=createdAt:desc&pagination[page]=${page}&pagination[pageSize]=100`, { credentials: 'include' });
    if (!r.ok) throw new Error(`Lecture refusée (${r.status})`);
    const d = await r.json();
    const lignes = (d?.data as Demande[]) || [];
    tout.push(...lignes);
    if (lignes.length < 100) break;
  }
  return tout;
}

/**
 * LE JOURNAL D'UNE DEMANDE — porté du site le 05/10/2026.
 *
 * ── Pourquoi il arrive ICI maintenant ──────────────────────────────────────
 *
 * « Demandes reçues » du site affichait ce journal ; cet écran-ci ne le
 * montrait pas. En retirant la gestion des demandes du site (Inès, 05/10/2026),
 * l'historique d'un rendez-vous n'aurait plus été lisible de NULLE PART — et il
 * vient justement de prendre de la valeur : la confirmation et ses corrections
 * (un rendez-vous déplacé, un lieu qui change) s'y enregistrent désormais, et
 * c'est le seul endroit qui dise QUI les a faites et QUAND.
 *
 * ── Ce qui n'a PAS été porté, et pourquoi ──────────────────────────────────
 *
 * Le site savait aussi poser des commentaires internes et ENVOYER une réponse
 * au patient (`demande-notes`). Mesuré avant la bascule, comme pour les
 * demandes de compte le 13/09 : la table n'en porte AUCUNE sur un rendez-vous
 * (seule une note existe, et elle concerne un compte). On ne reconstruit donc
 * pas un fil de discussion que personne n'a utilisé — mais c'est une capacité
 * RETIRÉE, pas une capacité absente : si le laboratoire se met à répondre aux
 * patients depuis l'application, elle est à reposer ici, et le module
 * `lib/suivi-demande.ts` sait déjà le faire (`cible: 'rdv'`).
 *
 * ── Chargé à l'OUVERTURE de la ligne, et pour elle seule ───────────────────
 *
 * Le filtrage est fait PAR LE SERVEUR sur la référence. Charger le registre
 * entier pour le filtrer dans le navigateur ne verrait que les dernières
 * entrées et raterait celles d'une demande ouverte il y a longtemps.
 */
function JournalDemande({ refDemande }: { refDemande: string }) {
  const [lignes, setLignes] = useState<LigneJournal[] | null>(null);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    /* ⚠️ Pose DIFFÉRÉE d'une micro-tâche, même précaution que l'écran des
       maintenances : un `setState` synchrone en tête d'effet déclenche un rendu
       en cascade, que React signale et que le lint du dépôt refuse. */
    if (!refDemande) {
      const t = setTimeout(() => setErreur('Cette demande n’a pas de référence : son journal ne peut pas être retrouvé.'), 0);
      return () => clearTimeout(t);
    }
    void journalDe(refDemande)
      .then((l) => { if (vivant) { setLignes(l); setErreur(''); } })
      .catch((e: Error) => { if (vivant) setErreur(e.message || 'Lecture impossible'); });
    return () => { vivant = false; };
  }, [refDemande]);

  /* ⚠️ UN ÉTAT VIDE EST UNE AFFIRMATION. Tant que la lecture n'a pas répondu,
     on ne peut pas écrire « aucune action » : ce serait affirmer qu'il ne s'est
     rien passé alors qu'on ne sait pas encore. Même règle que `enChargement`. */
  if (erreur) {
    return (
      <div style={{ fontSize: 12.5, color: 'var(--amber-600)', fontWeight: 600 }}>
        Le journal n’a pas pu être lu ({erreur}) — cette ligne ne dit donc <strong>pas</strong> qu’il ne s’est rien passé.
      </div>
    );
  }
  if (!lignes) return <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture du journal…</div>;
  if (!lignes.length) return <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune action enregistrée sur cette demande.</div>;

  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <span style={lbl}>Journal</span>
      {lignes.map((l, i) => (
        <div key={`${l.ts}-${i}`} style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--text-faint)', minWidth: 170 }}>
            {[l.who, depuis(l.createdAt || l.ts)].filter(Boolean).join(' · ')}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{l.detail}</span>
        </div>
      ))}
    </div>
  );
}

/** Un champ du pop-up : étiquette, contrôle, et le message d'erreur SOUS lui.
 *  Composant de module (et non défini dans le rendu de la fenêtre) : une
 *  fonction recréée à chaque frappe remonterait tout le sous-arbre et ferait
 *  perdre le focus au bout d'un caractère. */
function Champ({ id, label, requis, erreur, aide, children }: {
  id: string; label: string; requis?: boolean; erreur?: string | null;
  aide?: React.ReactNode; children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} style={{ ...lbl, display: 'block', marginBottom: 5 }}>
        {label}{requis && <span style={{ color: 'var(--red-600)' }}> *</span>}
      </label>
      {children}
      {aide && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{aide}</div>}
      <FieldError>{erreur || ''}</FieldError>
    </div>
  );
}

/**
 * LE POP-UP DU COUP DE FIL — ce qui a été recueilli, avant de confirmer.
 *
 * ── Pourquoi il s'ouvre AVANT l'écriture, et non après ─────────────────────
 *
 * Le statut ne part qu'avec les faits : `onEnregistrer` est appelé une seule
 * fois, avec `rdvStatus` ET `confirmation` dans le même corps. Confirmer
 * d'abord puis demander les informations aurait laissé, à chaque fenêtre
 * abandonnée, un rendez-vous « confirmé » dont personne ne connaît ni l'heure
 * ni le lieu — et l'écran n'aurait eu aucun moyen de distinguer ces demi-états
 * des demandes confirmées avant le 05/10/2026.
 *
 * ── Le téléphone est DANS la fenêtre ───────────────────────────────────────
 *
 * On remplit ce formulaire pendant l'appel, pas après : le numéro est donc
 * cliquable ici, avec ce que le site a déjà recueilli (nom tel qu'il a été
 * tapé, date souhaitée, créneau, analyses demandées). Sans ça il fallait
 * refermer la fenêtre pour relire la demande, et la refermer est précisément ce
 * que la règle du 04/09 rend coûteux.
 */
function FenetreConfirmation({ demande, aujourdhui, enregistre, refus, onFermer, onEnregistrer }: {
  demande: Demande;
  /** Passé, jamais lu ici : c'est ce qui rend la règle de la DDN testable. */
  aujourdhui: string;
  enregistre: boolean;
  /**
   * Le refus du serveur, RENDU DANS LA FENÊTRE.
   *
   * ⚠️ Sans cette propriété, le message partait dans le bandeau de l'écran —
   * c'est-à-dire DERRIÈRE le voile de la fenêtre, qui reste ouverte quand
   * l'écriture échoue (et elle doit le rester : refermer effacerait la saisie).
   * On avait donc une fenêtre qui ne réagissait plus et une explication que
   * personne ne pouvait lire. C'est la forme la plus coûteuse d'erreur : elle
   * est bien produite, et invisible.
   */
  refus: string;
  onFermer: () => void;
  onEnregistrer: (c: ConfirmationRdv) => void;
}) {
  const [saisie, setSaisie] = useState<SaisieConfirmation>(() => depart(demande));
  const champs = useMemo(() => champsConfirmation(saisie, aujourdhui), [saisie, aujourdhui]);
  /* Destructuré AVEC RENOMMAGE, comme les onze autres formulaires du dépôt :
     garder l'objet et lire `f.ref` en rendu fait échouer `react-hooks/refs`,
     qui ne distingue pas ce champ d'un vrai `useRef` déréférencé. */
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(champs);
  const maj = (p: Partial<SaisieConfirmation>) => setSaisie((x) => ({ ...x, ...p }));
  const err = (id: string) => champs.find((c) => c.id === id)?.erreur || '';
  const requis = (id: string) => !!champs.find((c) => c.id === id)?.requis;
  /* Une demande DÉJÀ confirmée qu'on rouvre : le geste n'est plus de confirmer,
     c'est de corriger un rendez-vous déplacé. Le verbe suit la conséquence
     (UI-CONVENTIONS §4) — « Enregistrer » ici, « Confirmer le rendez-vous » là. */
  const correction = estConfirme(demande.confirmation);
  const domicile = saisie.lieu === 'domicile';

  const soumettre = () => { if (verifier()) onEnregistrer(normaliser(saisie)); };

  return (
    <Modale
      titre={correction ? 'Corriger le rendez-vous' : 'Confirmer le rendez-vous'}
      largeur={620}
      onClose={onFermer}
      sousTitre={
        <span>
          <strong>{demande.ref}</strong> · déposée au nom de « {demande.patientName} » ·{' '}
          <a href={`tel:${(demande.patientPhone || '').replace(/[^+0-9]/g, '')}`} style={{ fontFamily: 'var(--font-mono)', direction: 'ltr', fontWeight: 700 }}>
            {demande.patientPhone || '—'}
          </a>
          {demande.preferredDate && <> · souhaitait le {dateFr(demande.preferredDate)}{demande.preferredSlot ? ` (${CRENEAUX[demande.preferredSlot] || demande.preferredSlot})` : ''}</>}
          {demande.requestedTests && <> · {demande.requestedTests}</>}
        </span>
      }
      pied={(
        <PiedActions
          annuler={{ onClick: onFermer, disabled: enregistre }}
          principale={{
            libelle: correction ? 'Enregistrer' : 'Confirmer le rendez-vous',
            onClick: soumettre,
            chargement: enregistre,
            libelleChargement: 'Enregistrement…',
          }}
        />
      )}
    >
      <div ref={refFormulaire} style={{ display: 'grid', gap: 14 }}>
        <Bandeau />
        {refus && (
          <div role="alert" style={{ background: 'var(--red-50)', color: 'var(--red-600)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', fontSize: 12.5, fontWeight: 600 }}>
            {refus} — rien n’a été enregistré. La saisie est conservée : réessayez.
          </div>
        )}
        <RequiredHint />
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5 }}>
          Ce que le patient a confirmé au téléphone. Le prénom et le nom sont
          <strong> proposés</strong> à partir du seul champ du site : à vérifier avec lui.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Champ id="rdv-conf-prenom" label="Prénom" requis erreur={err('rdv-conf-prenom')}>
            <input id="rdv-conf-prenom" style={{ ...inputStyle, width: '100%' }} value={saisie.prenom}
              onChange={(e) => maj({ prenom: e.target.value })} autoComplete="off" />
          </Champ>
          <Champ id="rdv-conf-nom" label="Nom" requis erreur={err('rdv-conf-nom')}>
            <input id="rdv-conf-nom" style={{ ...inputStyle, width: '100%' }} value={saisie.nom}
              onChange={(e) => maj({ nom: e.target.value })} autoComplete="off" />
          </Champ>
        </div>

        <Champ id="rdv-conf-naissance" label="Date de naissance" erreur={err('rdv-conf-naissance')}
          aide="Facultative — mais c’est le second identifiant du patient, et le téléphone est le dernier moment pour la demander.">
          <input id="rdv-conf-naissance" type="date" max={aujourdhui} style={{ ...inputStyle, width: 200 }}
            value={saisie.naissance} onChange={(e) => maj({ naissance: e.target.value })} />
        </Champ>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Champ id="rdv-conf-date" label="Date du rendez-vous" requis erreur={err('rdv-conf-date')}
            aide={demande.preferredDate && saisie.date && saisie.date !== jourIso(demande.preferredDate)
              ? `Déplacé — le patient avait demandé le ${dateFr(demande.preferredDate)}.` : undefined}>
            <input id="rdv-conf-date" type="date" style={{ ...inputStyle, width: '100%' }}
              value={saisie.date} onChange={(e) => maj({ date: e.target.value })} />
          </Champ>
          <Champ id="rdv-conf-heure" label="Heure du rendez-vous" requis erreur={err('rdv-conf-heure')}>
            <input id="rdv-conf-heure" type="time" style={{ ...inputStyle, width: '100%' }}
              value={saisie.heure} onChange={(e) => maj({ heure: e.target.value })} />
          </Champ>
        </div>

        {/* ⚠️ AUCUN LIEU PAR DÉFAUT : l'option vide est la première et reste
            sélectionnée tant qu'on n'a pas choisi. Démarrer sur « Au
            laboratoire » aurait enregistré ce lieu-là pour qui oublie de poser
            la question — le défaut le plus probable serait devenu le muet. */}
        <Champ id="rdv-conf-lieu" label="Lieu du rendez-vous" requis erreur={err('rdv-conf-lieu')}>
          <select id="rdv-conf-lieu" style={{ ...inputStyle, width: 260 }} value={saisie.lieu}
            onChange={(e) => maj({ lieu: e.target.value as LieuRdv | '' })}>
            <option value="">— à choisir —</option>
            {(Object.keys(LIBELLES_LIEU) as LieuRdv[]).map((k) => (
              <option key={k} value={k}>{LIBELLES_LIEU[k]}</option>
            ))}
          </select>
        </Champ>

        {/* L'adresse n'apparaît QUE pour un domicile. Toujours présente, elle
            aurait demandé l'adresse de quelqu'un qui vient au laboratoire ;
            c'est `champsConfirmation` qui porte la règle, et elle est éprouvée. */}
        {domicile && (
          <Champ id="rdv-conf-adresse" label="Adresse du domicile" requis={requis('rdv-conf-adresse')} erreur={err('rdv-conf-adresse')}
            aide="Où le préleveur doit se rendre : rue, numéro, étage, repère.">
            <textarea id="rdv-conf-adresse" rows={3} style={{ ...inputStyle, width: '100%', resize: 'vertical' }}
              value={saisie.adresse} onChange={(e) => maj({ adresse: e.target.value })} />
          </Champ>
        )}
      </div>
    </Modale>
  );
}

export function DemandesRdv() {
  const [liste, setListe] = useState<Demande[] | null>(null);
  const [erreur, setErreur] = useState('');
  const [filtre, setFiltre] = useState('encours');
  const [q, setQ] = useState('');
  const [ouverte, setOuverte] = useState<string | null>(null);
  /** La demande dont le pop-up de confirmation est ouvert. */
  const [aConfirmer, setAConfirmer] = useState<Demande | null>(null);
  /** Référence arrivée par la cloche, en attente que la liste soit chargée. */
  const [cibleRef, setCibleRef] = useState<string | null>(null);
  const { run, estOccupe } = useBusy();
  const refCible = useGed((st) => st.refCible);
  const setRefCible = useGed((st) => st.setRefCible);
  /* Lue UNE FOIS par montage, et passée au formulaire : la règle « une date de
     naissance n'est pas dans le futur » vit dans un module pur, qui ne lit pas
     l'horloge — sinon son test ne vaudrait que le jour où il a été écrit. */
  const aujourdhui = useMemo(() => aujourdhuiIso(), []);

  const recharger = useCallback(() => {
    void lireToutes().then(setListe).catch((e: Error) => { setErreur(e.message); setListe([]); });
  }, []);
  useEffect(() => { recharger(); }, [recharger]);

  /**
   * ARRIVER SUR LA DEMANDE, ET NON SUR LA LISTE.
   *
   * La cloche dépose ici avec la référence de la demande (`?ref=BIO24-RDV-…`,
   * rangée dans `refCible` par le Shell). Sans cet effet, le clic ouvrirait
   * l'écran et laisserait chercher la ligne à la main — exactement le défaut
   * corrigé le 13/09/2026 sur les autres écrans.
   *
   * ⚠️ ON DÉGAGE LES FILTRES. Une demande déjà clôturée vit dans
   * « Historique » : arriver sur elle avec le filtre « En cours » en place
   * donnerait une liste sans elle, et le lien se lirait comme mort.
   *
   * ⚠️ La référence est CONSOMMÉE (`setRefCible(null)`) : revenir sur l'écran
   * plus tard ne doit pas rouvrir la même demande. Pose différée d'une
   * micro-tâche, comme l'écran des maintenances — un `setState` synchrone en
   * tête d'effet déclenche un rendu en cascade que le lint du dépôt refuse.
   */
  useEffect(() => {
    if (!refCible) return undefined;
    const t = setTimeout(() => {
      setFiltre('toutes'); setQ('');
      setOuverte(null);
      setCibleRef(refCible);
      setRefCible(null);
    }, 0);
    return () => clearTimeout(t);
  }, [refCible, setRefCible]);

  /* La ligne à ouvrir est désignée par sa RÉFÉRENCE et non par son
     `documentId` : c'est la référence que porte la notification, et la liste
     peut n'être pas encore chargée quand elle arrive. */
  useEffect(() => {
    if (!cibleRef || !liste) return undefined;
    const trouvee = liste.find((d) => d.ref === cibleRef);
    const t = setTimeout(() => {
      /* Une référence introuvable NE SE TAIT PAS : sans ce message, le lien
         déposerait sur la liste complète et l'on croirait avoir mal cliqué. */
      if (!trouvee) setErreur(`La demande ${cibleRef} n’a pas été trouvée dans ce registre.`);
      else setOuverte(trouvee.documentId);
      setCibleRef(null);
    }, 0);
    return () => clearTimeout(t);
  }, [cibleRef, liste]);

  const filtrees = useMemo(() => {
    if (!liste) return [];
    const base = liste.filter((d) => (filtre === 'encours' ? EN_COURS.includes(d.rdvStatus) : filtre === 'historique' ? !EN_COURS.includes(d.rdvStatus) : true));
    const qq = q.trim().toLowerCase();
    /* L'identité et l'adresse RECUEILLIES entrent dans la recherche : c'est le
       nom corrigé au téléphone qu'on connaît ensuite, pas forcément celui qui a
       été tapé sur le site — et une tournée à domicile se retrouve par sa rue. */
    return qq ? base.filter((d) => `${d.ref} ${d.patientName} ${d.patientPhone} ${d.patientEmail} ${d.requestedTests} ${identiteConfirmee(d.confirmation)} ${d.confirmation?.adresse || ''}`.toLowerCase().includes(qq)) : base;
  }, [liste, filtre, q]);

  /**
   * Une écriture sur la demande, et une seule par geste.
   *
   * ⚠️ `champs` et non seulement `rdvStatus` : confirmer pose le statut ET ce
   * que le coup de fil a établi dans le MÊME corps. Deux appels enchaînés
   * auraient laissé, au premier échec réseau, un rendez-vous confirmé sans
   * heure ni lieu — exactement l'état que le pop-up existe pour empêcher.
   */
  const ecrire = (d: Demande, cle: string, champs: Partial<Demande>) => run(`${d.documentId}-${cle}`, async () => {
    setErreur('');
    const r = await fetchEcriture(`/api/ged/appointments/${d.documentId}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data: champs }),
    });
    if (!r.ok) { setErreur(`Mise à jour refusée (${r.status}).`); return; }
    setListe((l) => (l ? l.map((x) => (x.documentId === d.documentId ? { ...x, ...champs } : x)) : l));
    setAConfirmer(null);
  });

  const changerStatut = (d: Demande, rdvStatus: Statut) => ecrire(d, rdvStatus, { rdvStatus });

  /* Confirmer ET corriger passent par ici. La différence n'est pas dans
     l'écriture mais dans le STATUT : une demande déjà confirmée ne repasse pas
     par `new → confirmed`, on ne fait que remplacer ce qui a été recueilli. */
  const enregistrerConfirmation = (d: Demande, confirmation: ConfirmationRdv) => ecrire(
    d, 'confirmation',
    estConfirme(d.confirmation) ? { confirmation } : { rdvStatus: 'confirmed', confirmation },
  );

  const voirOrdonnance = async (d: Demande) => {
    const r = await fetch(`/api/ged/appointments?filters[ref][$eq]=${encodeURIComponent(d.ref)}&fields[0]=prescriptionFileData`, { credentials: 'include' });
    const data = String((await r.json().catch(() => null))?.data?.[0]?.prescriptionFileData || '');
    if (!data) { setErreur('Ordonnance introuvable.'); return; }
    // Ouverte dans un onglet, à partir d'un Blob : un data-URL de plusieurs Mo
    // en adresse est refusé par les navigateurs récents.
    const [entete, b64] = data.split(',');
    const type = /data:([^;]+)/.exec(entete || '')?.[1] || 'application/octet-stream';
    const octets = Uint8Array.from(atob(b64 || ''), (c) => c.charCodeAt(0));
    window.open(URL.createObjectURL(new Blob([octets], { type })), '_blank', 'noopener');
  };

  if (!liste) {
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <H1 eyebrow="Patients · site public" title="Demandes de rendez-vous" />
        <div style={card}><Skeleton rows={4} height={52} /></div>
      </div>
    );
  }
  const nb = (s: Statut[]) => liste.filter((d) => s.includes(d.rdvStatus)).length;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <H1 eyebrow="Patients · site public" title="Demandes de rendez-vous" />
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.5, maxWidth: '84ch' }}>
        Les demandes déposées sur le site, par le formulaire ou la fenêtre <strong>« Prendre rendez-vous »</strong>.
        C’est <strong>le seul endroit</strong> où elles se traitent : le site les reçoit, il ne les gère plus.
        Chaque nouvelle demande est notifiée par e-mail à l’adresse réglée dans le site (Paramétrage › Notifications).
        « Confirmer » ouvre une fenêtre pour y noter ce que le patient dit au téléphone — heure, lieu,
        et l’adresse s’il s’agit d’un prélèvement à domicile.
      </p>

      {erreur && <div style={{ background: 'var(--red-50)', color: 'var(--red-600)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', fontSize: 12.5, fontWeight: 600 }}>{erreur}</div>}

      {liste.length === 0 ? (
        <EmptyState titre="Aucune demande de rendez-vous." indice="Les demandes déposées sur le site apparaîtront ici dès leur envoi." />
      ) : (
        <>
          <FilterBar
            q={q} onQ={setQ} placeholder="Rechercher : nom, téléphone, référence, analyse…"
            chips={[
              { k: 'encours', label: 'En cours', n: nb(EN_COURS) },
              { k: 'historique', label: 'Historique', n: liste.length - nb(EN_COURS) },
              { k: 'toutes', label: 'Toutes', n: liste.length },
            ]}
            actif={filtre} onChip={setFiltre}
            total={liste.length} affiches={filtrees.length}
            onReset={() => { setQ(''); setFiltre('encours'); }} />

          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            {filtrees.map((d, i) => {
              const s = STATUTS[d.rdvStatus] || STATUTS.new;
              const est = ouverte === d.documentId;
              return (
                <div key={d.documentId} style={{ borderTop: i ? '1px solid var(--border-subtle)' : 'none' }}>
                  <button type="button" onClick={() => setOuverte(est ? null : d.documentId)} className="ged-row" aria-expanded={est}
                    style={{ display: 'block', width: '100%', textAlign: 'start', font: 'inherit', fontFamily: 'var(--font-sans)', background: 'none', border: 'none', padding: '11px 14px', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{d.ref}</span>
                      <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: s.bg, color: s.fg }}>{s.label}</span>
                      <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>{d.patientName}</strong>
                      {/* Le rendez-vous CONVENU chasse la date souhaitée dès
                          qu'il existe : c'est lui qu'on vient lire. Le souhait
                          n'a plus d'intérêt qu'en l'absence de confirmation —
                          ou pour constater un déplacement, et c'est alors le
                          détail dépliant qui le dit. */}
                      <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
                        {estConfirme(d.confirmation)
                          ? <>rendez-vous <strong>{resumeConfirmation(d.confirmation)}</strong></>
                          : <>souhaite le <strong>{dateFr(d.preferredDate)}</strong>{d.preferredSlot ? ` · ${CRENEAUX[d.preferredSlot] || d.preferredSlot}` : ''}</>}
                      </span>
                      <span style={{ flex: 1 }} />
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>déposée le {depuis(d.createdAt)}</span>
                    </div>
                    {d.requestedTests && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{d.requestedTests}</div>}
                  </button>

                  {est && (
                    <div style={{ padding: '0 14px 14px', display: 'grid', gap: 10, fontSize: 12.5 }}>
                      {/* CE QUE LE COUP DE FIL A ÉTABLI — en tête du détail, et
                          sur fond pour se distinguer de ce que le site a
                          déclaré. Les deux ne sont pas de même valeur : l'un a
                          été vérifié avec le patient, l'autre tapé par lui dans
                          un formulaire public. */}
                      {estConfirme(d.confirmation) && (
                        <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '9px 12px', display: 'grid', gap: 3 }}>
                          <div><span style={lbl}>Rendez-vous convenu</span> — <strong>{resumeConfirmation(d.confirmation)}</strong></div>
                          <div>
                            <span style={lbl}>Patient</span> — {identiteConfirmee(d.confirmation) || '—'}
                            {d.confirmation?.naissance ? ` · né(e) le ${dateFr(d.confirmation.naissance)}` : ''}
                          </div>
                          {jourIso(d.preferredDate || '') !== d.confirmation?.date && d.preferredDate && (
                            <div style={{ color: 'var(--text-muted)' }}>Le patient avait demandé le {dateFr(d.preferredDate)}{d.preferredSlot ? ` (${CRENEAUX[d.preferredSlot] || d.preferredSlot})` : ''}.</div>
                          )}
                        </div>
                      )}
                      {/* Une demande confirmée AVANT le 05/10/2026 ne porte
                          rien : le dire, plutôt que laisser un blanc qui se lit
                          comme « rien n'a été recueilli ». */}
                      {d.rdvStatus !== 'new' && !estConfirme(d.confirmation) && (
                        <div style={{ color: 'var(--text-muted)' }}>
                          Aucune information de rendez-vous enregistrée — demande traitée avant la mise en place du recueil téléphonique.
                        </div>
                      )}
                      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                        <span>Téléphone : <a href={`tel:${d.patientPhone.replace(/[^+0-9]/g, '')}`} style={{ fontFamily: 'var(--font-mono)', direction: 'ltr' }}>{d.patientPhone}</a></span>
                        <span>E-mail : {d.patientEmail ? <a href={`mailto:${d.patientEmail}`} style={{ direction: 'ltr' }}>{d.patientEmail}</a> : '—'}</span>
                      </div>
                      {d.notes && <div style={{ whiteSpace: 'pre-wrap', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-sm)', padding: '8px 11px' }}>{d.notes}</div>}
                      {d.prescriptionFileName && (
                        <div>Ordonnance : <Bouton taille="sm" type="button" onClick={() => void voirOrdonnance(d)}>Voir « {d.prescriptionFileName} »</Bouton></div>
                      )}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {/* Le bouton n'ÉCRIT PLUS : il ouvre le pop-up, qui
                            seul sait poser le statut avec ce qu'on a recueilli. */}
                        {d.rdvStatus === 'new' && <Bouton variante="primaire" taille="sm" type="button" disabled={estOccupe()} onClick={() => setAConfirmer(d)}>Confirmer (patient rappelé)</Bouton>}
                        {/* Un rendez-vous se déplace : on rouvre la même fenêtre
                            pour corriger l'heure ou le lieu, sans toucher au
                            statut. Le journal enregistre la correction. */}
                        {estConfirme(d.confirmation) && EN_COURS.includes(d.rdvStatus) && (
                          <Bouton taille="sm" type="button" disabled={estOccupe()} onClick={() => setAConfirmer(d)}>Corriger le rendez-vous</Bouton>
                        )}
                        {EN_COURS.includes(d.rdvStatus) && <Bouton taille="sm" type="button" disabled={estOccupe()} onClick={() => changerStatut(d, 'done')}>Marquer traitée</Bouton>}
                        {EN_COURS.includes(d.rdvStatus) && <Bouton taille="sm" type="button" disabled={estOccupe()} onClick={() => changerStatut(d, 'cancelled')}>Annuler</Bouton>}
                        {!EN_COURS.includes(d.rdvStatus) && d.rdvStatus !== 'archived' && <Bouton taille="sm" type="button" disabled={estOccupe()} onClick={() => changerStatut(d, 'archived')}>Archiver</Bouton>}
                        {!EN_COURS.includes(d.rdvStatus) && <Bouton taille="sm" type="button" disabled={estOccupe()} onClick={() => changerStatut(d, 'new')}>Rouvrir</Bouton>}
                      </div>

                      {/* `key` composée de ce qui est JOURNALISÉ : confirmer ou
                          corriger remonte le composant, qui relit le journal.
                          Sans ça, la ligne qu'on vient d'écrire n'apparaîtrait
                          qu'au rechargement de la page — et l'absence se
                          lirait comme « ça n'a pas été tracé ». */}
                      <JournalDemande
                        key={[d.ref, d.rdvStatus, d.confirmation?.date, d.confirmation?.heure, d.confirmation?.lieu, d.confirmation?.adresse].join('|')}
                        refDemande={d.ref}
                      />
                    </div>
                  )}
                </div>
              );
            })}
            {filtrees.length === 0 && <div style={{ padding: 14, fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune demande dans ce filtre.</div>}
          </div>
        </>
      )}

      {/* `key` sur la référence : ouvrir une AUTRE demande doit repartir d'un
          formulaire neuf. Sans elle, React réutiliserait l'état interne et la
          fenêtre s'ouvrirait sur l'heure et le lieu du patient précédent. */}
      {aConfirmer && (
        <FenetreConfirmation
          key={aConfirmer.documentId}
          demande={aConfirmer}
          aujourdhui={aujourdhui}
          enregistre={estOccupe(`${aConfirmer.documentId}-confirmation`)}
          refus={erreur}
          onFermer={() => { setErreur(''); setAConfirmer(null); }}
          onEnregistrer={(c) => enregistrerConfirmation(aConfirmer, c)}
        />
      )}
    </div>
  );
}
